import { Plan } from "types/Plan";
import { PlanIcon } from "../plan/PlanIcon";

interface BenefitCardProps {
    plan: Plan;
    isSelected: boolean;
    onChange?: () => void;
}

/**
 * BenefitCard component for displaying MLTA plan options (Superior/Premier)
 * Renders a selectable card with plan sections, benefits, and pricing
 */
export const BenefitCard = ({ plan, isSelected, onChange = () => {} }: BenefitCardProps) => {
    const radioId = `plan-${plan.id}`;

    const handleClick = (e: React.MouseEvent) => {
        // Only prevent default if clicking on the label itself (not the radio input)
        // This prevents the scroll-to-radio behavior while allowing direct radio clicks to work
        if ((e.target as HTMLElement).tagName !== "INPUT") {
            e.preventDefault();
            onChange();
        }
    };

    return (
        <label
            htmlFor={radioId}
            onClick={handleClick}
            className={`${
                isSelected ? "bg-neutral" : "bg-base-100"
            } rounded-2xl w-full flex flex-col overflow-hidden border-2 cursor-pointer ${
                isSelected ? "border-primary" : "border-info"
            }`}
        >
            <div className="p-4 flex items-center justify-center">
                <div className="flex items-center gap-2">
                    <div
                        className={`w-4 h-4 rounded-full border-2 border-primary flex-shrink-0 flex items-center justify-center`}
                    >
                        {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <input
                        type="radio"
                        id={radioId}
                        name="plan"
                        value={plan.id}
                        checked={isSelected}
                        onChange={onChange}
                        className="sr-only"
                    />
                    <span className="text-lg font-bold text-base-content">{plan.name}</span>
                </div>
            </div>

            <div className="px-4 flex flex-col">
                {plan.sections.map((section, sectionIndex) => (
                    <div key={section.title}>
                        {sectionIndex > 0 && (
                            <div className={`border-t-2 -mx-4 ${isSelected ? "border-primary" : "border-info"}`}></div>
                        )}
                        <div className={`flex flex-col gap-2 ${sectionIndex === 0 ? "pb-2" : "py-2"}`}>
                            <h3 className="text-xs font-medium text-gray-500 text-center">{section.title}</h3>
                            {section.benefits.map((benefit) => (
                                <div key={`${benefit.icon}-${benefit.amount}`} className="flex flex-col gap-1">
                                    <div className="flex gap-1 items-center">
                                        <PlanIcon
                                            iconName={benefit.icon}
                                            className={
                                                benefit.icon === "Fall" ||
                                                benefit.icon === "Bruised" ||
                                                benefit.icon === "Bike" ||
                                                benefit.icon === "Airplane" ||
                                                benefit.icon === "Heart"
                                                    ? "w-4 h-4"
                                                    : "w-3 h-3"
                                            }
                                        />
                                        <span className="text-sm font-bold text-base-content whitespace-nowrap">
                                            {benefit.amount}
                                        </span>
                                    </div>
                                    <span className="text-sm font-normal text-base-content pl-4">{benefit.description}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className={`rounded-b-xl h-[52px] ${isSelected ? "bg-primary" : "bg-neutral"}`}>
                <div className="text-center h-full flex items-center justify-center">
                    <span className={`text-lg font-bold ${isSelected ? "text-neutral" : "text-primary"}`}>
                        ${plan.monthlyPrice}/month
                    </span>
                </div>
            </div>
        </label>
    );
};
